import { HttpErrorResponse } from '@angular/common/http';
import { ErrorTranslation } from '../models/error-translation.model';

/**
 * Base error translations for common HTTP errors.
 * These can be overridden by domain or app-specific translations.
 */
export const BASE_ERROR_TRANSLATIONS: ErrorTranslation[] = [
  // Network Errors
  {
    code: 'common.network.offline',
    message:
      'Unable to connect to the server. Please check your internet connection and try again.',
    title: 'Connection Error',
    matcher: error => error.status === 0,
    priority: 0,
  },

  // Authentication Errors (4xx)
  // Note: Backend wraps errors as { error: { message: "..." } }, so we check error.error?.error?.message
  {
    code: 'common.auth.invalid_credentials',
    message:
      'The email or password you entered is incorrect. Please try again.',
    title: 'Invalid Credentials',
    matcher: error =>
      error.status === 401 &&
      (error.error?.error?.message === 'Invalid credentials' ||
        error.error?.message === 'Invalid credentials'),
    priority: 8,
  },
  {
    code: 'common.auth.reset_token_expired',
    message: 'This password reset link has expired. Please request a new one.',
    title: 'Reset Link Expired',
    matcher: error =>
      error.status === 401 &&
      (error.error?.error?.message === 'Reset token has expired' ||
        error.error?.message === 'Reset token has expired'),
    priority: 8,
  },
  {
    code: 'common.auth.unauthorized',
    message: 'Your session has expired. Please log in again to continue.',
    title: 'Session Expired',
    matcher: error =>
      error.status === 401 &&
      (error.error?.error?.message === 'Unauthorized' ||
        error.error?.message === 'Unauthorized' ||
        (!error.error?.error?.message && !error.error?.message)),
    priority: 5,
  },
  {
    code: 'common.auth.generic_unauthorized',
    message:
      'You are not authorized to access this resource. Please log in and try again.',
    title: 'Unauthorized',
    matcher: error => error.status === 401,
    priority: 0,
  },
  {
    code: 'common.auth.forbidden',
    message:
      'You do not have permission to access this resource. Please contact your administrator if you believe this is an error.',
    title: 'Access Denied',
    matcher: error => error.status === 403,
    priority: 0,
  },

  // Client Errors (4xx)
  {
    code: 'common.client.not_found',
    message:
      'The requested resource could not be found. It may have been moved or deleted.',
    title: 'Not Found',
    matcher: error => error.status === 404,
    priority: 0,
  },
  {
    code: 'common.client.bad_request',
    message:
      'The request could not be processed. Please check your input and try again.',
    title: 'Invalid Request',
    matcher: error => error.status === 400,
    priority: 0,
  },
  {
    code: 'common.client.conflict',
    message:
      'The operation could not be completed due to a conflict. The resource may have been modified by another user.',
    title: 'Conflict Detected',
    matcher: error => error.status === 409,
    priority: 0,
  },
  {
    code: 'common.client.validation',
    message:
      'The submitted data is invalid. Please check the highlighted fields and try again.',
    title: 'Validation Error',
    matcher: error => error.status === 422,
    priority: 0,
  },
  {
    code: 'common.client.timeout',
    message: 'The request took too long to complete. Please try again.',
    title: 'Request Timeout',
    matcher: error => error.status === 408,
    priority: 0,
  },
  {
    code: 'common.client.rate_limit',
    message: 'Too many requests. Please wait a moment and try again.',
    title: 'Rate Limited',
    matcher: error => error.status === 429,
    priority: 0,
  },

  // Server Errors (5xx)
  {
    code: 'common.server.internal',
    message:
      'An unexpected error occurred on the server. Our team has been notified. Please try again later.',
    title: 'Server Error',
    matcher: error => error.status === 500,
    priority: 0,
  },
  {
    code: 'common.server.unavailable',
    message:
      'The service is temporarily unavailable. Please try again in a few minutes.',
    title: 'Service Unavailable',
    matcher: error => error.status === 503,
    priority: 0,
  },
  {
    code: 'common.server.maintenance',
    message:
      'The system is currently under maintenance. Please check back later.',
    title: 'Maintenance Mode',
    matcher: error =>
      error.status === 503 && !!error.headers?.get('Retry-After'),
    priority: 1, // Higher priority than generic 503
  },
  {
    code: 'common.server.gateway',
    message: 'Unable to reach the server. Please try again later.',
    title: 'Gateway Error',
    matcher: error => error.status === 502,
    priority: 0,
  },
  {
    code: 'common.server.gateway_timeout',
    message:
      'The server is taking too long to respond. Please try again later.',
    title: 'Gateway Timeout',
    matcher: error => error.status === 504,
    priority: 0,
  },

  // Generic Fallback
  {
    code: 'common.generic.error',
    message:
      'An unexpected error occurred. Please try again or contact support if the problem persists.',
    title: 'Error',
    matcher: () => true, // Always matches as final fallback
    priority: -1, // Lowest priority
  },
];

/**
 * Get a base translation by status code (for testing/reference)
 */
export function getBaseTranslationByStatus(
  status: number,
): ErrorTranslation | undefined {
  return BASE_ERROR_TRANSLATIONS.find(t => {
    if (t.matcher) {
      const mockError = { status } as HttpErrorResponse;
      return t.matcher(mockError);
    }
    return false;
  });
}
